import { useMemo } from "react";

import type { PermissionAction, PermissionResource } from "@lib/permissionConstants";
import { PERMISSION_CONFIGS } from "@lib/permissionConstants";
import sharedConstants from "@lib/shared_constants";

import type { RealPermission, RealPermissionValue } from "@services/permissions/types";
import { useFeatureOn } from "@services/useFeatureOn";
import useI18nNamespace from "@services/useI18nNamespace";

type ActionPermissionValuesMap = {
  [A in PermissionAction]: RealPermission;
};

type ResourceActionPermissionValuesMap = {
  [R in PermissionResource]: ActionPermissionValuesMap;
};

type ReadonlyResourceActionPermissionValuesMap = Readonly<{
  [R in PermissionResource]: Readonly<{
    [A in PermissionAction]: Readonly<RealPermission>;
  }>;
}>;

/**
 * Returns a nested map of PermissionResource to PermissionAction to Permission. Provides a view of all Actions
 * available for each resource and which Permissions gate those actions.
 */
export const useResourceActionPermissionValuesMap = (
  permissionValueToPermission: (permissionValue: RealPermissionValue) => RealPermission
) => {
  const { t: permissionsTranslator } = useI18nNamespace("permission-constants");
  const featureOn = useFeatureOn();

  const resourceActionToPermissionValueMap = useMemo(() => {
    return PERMISSION_CONFIGS(permissionsTranslator, featureOn as (feature: string) => boolean).reduce(
      (acc, permissionConfig) => {
        if (
          permissionConfig.resource &&
          permissionConfig.action &&
          permissionConfig.permission !== sharedConstants.PERMISSIONS.PERMISSIONS_BY_VALUE.placeholder
        ) {
          (acc[permissionConfig.resource] ??= {} as ActionPermissionValuesMap)[permissionConfig.action] =
            permissionValueToPermission(permissionConfig.permission);
        }

        return acc;
      },
      {} as ResourceActionPermissionValuesMap
    ) as ReadonlyResourceActionPermissionValuesMap;
  }, [permissionsTranslator, featureOn, permissionValueToPermission]);

  return resourceActionToPermissionValueMap;
};
